// cSpell:ignore elkjs xyflow layouted Layouting Layouted
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ELK, { type ElkNode } from "elkjs/lib/elk.bundled.js";
import { Position, type Edge, type Node } from "@xyflow/react";

import type { BuiltGraph, GraphNode, GraphNodeData } from "../utils/buildGraph";
import {
  buildElkLayoutOptions,
  elkGroupLayoutOptions,
  type ElkDirection,
} from "../utils/elkOptions";

const elk = new ELK();

interface UseElkLayoutResult {
  nodes: Node<GraphNodeData>[];
  edges: Edge[];
  isLayouting: boolean;
}

export type { ElkDirection };

/**
 * Layout du diagramme :
 *  1. Première passe ELK avec les hauteurs estimées (buildGraph) — affiche
 *     un layout cohérent dès le premier rendu.
 *  2. Un ResizeObserver suit les `<div class="react-flow__node">` rendus dans
 *     le DOM. Dès qu'une taille change, on re-lance ELK avec les vraies
 *     dimensions. On répète jusqu'à stabilisation, ce qui garantit un espace
 *     constant entre les nodes peu importe la longueur du contenu (texte
 *     traduit, fonts, etc.).
 */
export function useElkLayout(
  graph: BuiltGraph,
  direction: ElkDirection = "DOWN",
): UseElkLayoutResult {
  const { t, i18n } = useTranslation();

  const [layouted, setLayouted] = useState<UseElkLayoutResult>({
    nodes: [],
    edges: [],
    isLayouting: true,
  });
  const lastRunId = useRef(0);
  // Dernières tailles utilisées pour faire tourner ELK — sert à éviter de
  // relancer le layout quand les mesures n'ont pas vraiment changé.
  const lastSizes = useRef<Map<string, { width: number; height: number }>>(
    new Map(),
  );

  // Première passe avec hauteurs estimées.
  useEffect(() => {
    lastSizes.current = new Map();
    runLayout({
      runIdRef: lastRunId,
      graph,
      direction,
      sizeOverrides: null,
      t,
      onResult: setLayouted,
    });
  }, [graph, direction, t, i18n.language]);

  // Surveiller les vraies dimensions DOM via ResizeObserver. À chaque
  // changement stabilisé, on relance ELK.
  useEffect(() => {
    if (graph.nodes.length === 0) return;

    let rafId: number | null = null;
    const measured = new Map<string, { width: number; height: number }>();

    const scheduleLayout = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (measured.size !== graph.nodes.length) return;
        if (sameSizes(measured, lastSizes.current)) return;
        // Snapshot des tailles utilisées pour cette passe.
        lastSizes.current = new Map(measured);
        runLayout({
          runIdRef: lastRunId,
          graph,
          direction,
          sizeOverrides: new Map(measured),
          t,
          onResult: setLayouted,
        });
      });
    };

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const id = (entry.target as HTMLElement).getAttribute("data-id");
        if (!id) continue;
        const rect = entry.contentRect;
        // On utilise offsetWidth/Height (incl. border) plutôt que contentRect
        // pour matcher ce qu'ELK attend (la boîte complète du node).
        const el = entry.target as HTMLElement;
        measured.set(id, {
          width: el.offsetWidth || rect.width,
          height: el.offsetHeight || rect.height,
        });
      }
      scheduleLayout();
    });

    // Polling court pour attacher le ResizeObserver dès que les nodes
    // apparaissent dans le DOM (React Flow les rend après le premier layout).
    const attach = () => {
      const els = document.querySelectorAll<HTMLElement>(
        ".react-flow__node[data-id]",
      );
      if (els.length === 0) return false;
      els.forEach((el) => {
        const id = el.getAttribute("data-id");
        if (!id) return;
        measured.set(id, {
          width: el.offsetWidth,
          height: el.offsetHeight,
        });
        observer.observe(el);
      });
      scheduleLayout();
      return true;
    };

    let attachInterval: number | null = null;
    if (!attach()) {
      attachInterval = window.setInterval(() => {
        if (attach() && attachInterval !== null) {
          clearInterval(attachInterval);
          attachInterval = null;
        }
      }, 50);
    }

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (attachInterval !== null) clearInterval(attachInterval);
      observer.disconnect();
    };
  }, [graph, direction, t]);

  return layouted;
}

function sameSizes(
  a: Map<string, { width: number; height: number }>,
  b: Map<string, { width: number; height: number }>,
): boolean {
  if (a.size !== b.size) return false;
  for (const [id, sizeA] of a) {
    const sizeB = b.get(id);
    if (!sizeB) return false;
    if (
      Math.abs(sizeA.width - sizeB.width) > 0.5 ||
      Math.abs(sizeA.height - sizeB.height) > 0.5
    ) {
      return false;
    }
  }
  return true;
}

interface RunLayoutArgs {
  runIdRef: React.RefObject<number>;
  graph: BuiltGraph;
  direction: ElkDirection;
  sizeOverrides: Map<string, { width: number; height: number }> | null;
  t: ReturnType<typeof useTranslation>["t"];
  onResult: (next: UseElkLayoutResult) => void;
}

interface LayoutResult {
  x: number;
  y: number;
  width: number;
  height: number;
}

function buildElkNode(
  node: GraphNode,
  childrenByParent: Map<string, GraphNode[]>,
  sizeOverrides: Map<string, { width: number; height: number }> | null,
): ElkNode {
  const override = sizeOverrides?.get(node.id);
  const children = childrenByParent.get(node.id) ?? [];
  const elkNode: ElkNode = {
    id: node.id,
    width: override?.width ?? node.width,
    height: override?.height ?? node.height,
  };
  if (children.length > 0) {
    elkNode.layoutOptions = { ...elkGroupLayoutOptions };
    elkNode.children = children.map((c) =>
      buildElkNode(c, childrenByParent, sizeOverrides),
    );
  }
  return elkNode;
}

function collectLayout(
  elkChildren: ElkNode[] | undefined,
  accumulator: Map<string, LayoutResult>,
): void {
  for (const c of elkChildren ?? []) {
    accumulator.set(c.id, {
      x: c.x ?? 0,
      y: c.y ?? 0,
      width: c.width ?? 0,
      height: c.height ?? 0,
    });
    if (c.children) collectLayout(c.children, accumulator);
  }
}

function runLayout({
  runIdRef,
  graph,
  direction,
  sizeOverrides,
  t,
  onResult,
}: RunLayoutArgs): void {
  const runId = ++runIdRef.current;
  const isHorizontal = direction === "RIGHT";
  const sourcePos = isHorizontal ? Position.Right : Position.Bottom;
  const targetPos = isHorizontal ? Position.Left : Position.Top;

  // Regroupe les nodes par parentId pour reconstruire un arbre ELK récursif.
  const childrenByParent = new Map<string, GraphNode[]>();
  for (const n of graph.nodes) {
    if (!n.parentId) continue;
    const arr = childrenByParent.get(n.parentId) ?? [];
    arr.push(n);
    childrenByParent.set(n.parentId, arr);
  }
  const rootNodes = graph.nodes.filter((n) => !n.parentId);

  const elkGraph: ElkNode = {
    id: "root",
    layoutOptions: buildElkLayoutOptions(direction),
    children: rootNodes.map((n) =>
      buildElkNode(n, childrenByParent, sizeOverrides),
    ),
    edges: graph.edges.map((e) => ({
      id: e.id,
      sources: [e.source],
      targets: [e.target],
    })),
  };

  elk
    .layout(elkGraph)
    .then((res) => {
      if (runId !== runIdRef.current) return;

      const layouts = new Map<string, LayoutResult>();
      collectLayout(res.children, layouts);

      const nextNodes: Node<GraphNodeData>[] = graph.nodes.map((n) => {
        const layout = layouts.get(n.id) ?? {
          x: 0,
          y: 0,
          width: n.width,
          height: n.height,
        };
        const isGroup = n.data.kind === "branchGroup";
        return {
          id: n.id,
          type: n.data.kind,
          position: { x: layout.x, y: layout.y },
          data: n.data,
          parentId: n.parentId,
          extent: n.parentId ? ("parent" as const) : undefined,
          sourcePosition: sourcePos,
          targetPosition: targetPos,
          // initialWidth/initialHeight sont des hints pour le premier rendu et
          // la MiniMap. React Flow re-mesure ensuite la vraie taille DOM.
          initialWidth: isGroup ? layout.width : n.width,
          initialHeight: isGroup ? layout.height : n.height,
          style: isGroup
            ? { width: layout.width, height: layout.height }
            : { width: n.width },
          draggable: false,
          selectable: false,
          connectable: false,
        };
      });

      const nodeKindById = new Map(graph.nodes.map((n) => [n.id, n.data.kind]));

      const nextEdges: Edge[] = graph.edges.map((e) => {
        const branchLabel =
          e.branch === "if"
            ? t("step5Validation.branches.yes")
            : e.branch === "else"
              ? t("step5Validation.branches.no")
              : undefined;

        // Les edges issus d'un nœud "conditions" utilisent les handles nommés.
        const sourceKind = nodeKindById.get(e.source);
        const sourceHandle =
          sourceKind === "conditions" &&
          (e.branch === "if" || e.branch === "else")
            ? e.branch
            : undefined;

        return {
          id: e.id,
          source: e.source,
          target: e.target,
          sourceHandle,
          type: "branch",
          data: {
            branch: e.branch ?? "main",
            label: branchLabel,
            path: "smoothstep" as const,
          },
        };
      });

      onResult({
        nodes: nextNodes,
        edges: nextEdges,
        isLayouting: false,
      });
    })
    .catch((err) => {
      if (runId !== runIdRef.current) return;
      console.error("[useElkLayout] layout failed", err);
      onResult({
        nodes: [],
        edges: [],
        isLayouting: false,
      });
    });
}
